/**
 * CTA band.
 *
 * <cta-band-marquee> - endless right-to-left strip of videos/images.
 * The original items are cloned until the track is wide enough to loop
 * seamlessly, then a CSS animation translates it by exactly one set width.
 * The animation only runs while the section is on screen, and each video
 * only plays while it is visible, so off-screen tiles cost nothing.
 *
 * With [data-static] the strip is not cloned or animated - only the video
 * playback handling is kept.
 */
(function () {
  'use strict';

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  if (customElements.get('cta-band-marquee')) return;

  /**
   * [data-cta-band-scroll] buttons - "top" scrolls to the top of the page,
   * "grid" to the first main collection product grid, offset by the sticky
   * header. If there is no grid on the page the button's link is followed.
   */
  function getStickyHeaderOffset(targetY) {
    // Split-nav header: only the menu bar is pinned (see sections/header.liquid),
    // and only once the page is scrolled past 200px. Announcement + perks scroll away.
    const abMenu = document.querySelector('body.header-split-nav .header-ab-sticky');
    if (abMenu) return targetY > 200 ? abMenu.offsetHeight : 0;

    if (!document.documentElement.classList.contains('sticky-header-enabled')) return 0;
    const header = document.querySelector('.shopify-section.header__outer-wrapper');
    return header ? header.offsetHeight : 0;
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-cta-band-scroll]');
    if (!button) return;

    const behavior = reducedMotion.matches ? 'auto' : 'smooth';

    if (button.dataset.ctaBandScroll === 'top') {
      event.preventDefault();
      window.scrollTo({ top: 0, behavior });
      return;
    }

    const grid = document.querySelector('[data-main-collection-grid]');
    if (!grid) return;

    event.preventDefault();
    const gridTop = grid.getBoundingClientRect().top + window.scrollY;
    let top = gridTop - getStickyHeaderOffset(gridTop);
    // The menu only pins past 200px - if subtracting it lands above that, it won't be pinned there
    if (top <= 200) top = gridTop - getStickyHeaderOffset(top);
    window.scrollTo({ top: Math.max(top, 0), behavior });
  });

  class CtaBandMarquee extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;

      this.isStatic = this.hasAttribute('data-static');
      this.speed = parseFloat(this.dataset.speed) || 40;

      this.videoObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting) {
            const playing = video.play();
            if (playing) playing.catch(() => {});
          } else {
            video.pause();
          }
        });
      });

      this.sectionObserver = new IntersectionObserver(([entry]) => {
        this.classList.toggle('is-running', entry.isIntersecting);
      });
      this.sectionObserver.observe(this);

      this.build();

      // Only the container width matters - rebuild when it changes enough to
      // need a different number of clones (e.g. crossing the desktop breakpoint).
      this.lastWidth = this.clientWidth;
      this.resizeObserver = new ResizeObserver(() => {
        if (Math.abs(this.clientWidth - this.lastWidth) < 1) return;
        this.lastWidth = this.clientWidth;
        window.cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = window.requestAnimationFrame(() => this.build());
      });
      this.resizeObserver.observe(this);

      // Theme editor: items can be added, removed or reordered without the
      // whole section re-rendering, so rebuild the clones when that happens.
      if (window.Shopify && window.Shopify.designMode) {
        this.mutationObserver = new MutationObserver((mutations) => {
          const external = mutations.some((mutation) =>
            [...mutation.addedNodes, ...mutation.removedNodes].some(
              (node) => node.nodeType === 1 && !node.hasAttribute('data-marquee-clone')
            )
          );
          if (external) this.build();
        });
        this.mutationObserver.observe(this.track, { childList: true });
      }

      this._onMotionChange = () => this.build();
      reducedMotion.addEventListener('change', this._onMotionChange);
    }

    disconnectedCallback() {
      if (this.videoObserver) this.videoObserver.disconnect();
      if (this.sectionObserver) this.sectionObserver.disconnect();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.mutationObserver) this.mutationObserver.disconnect();
      if (this._onMotionChange) reducedMotion.removeEventListener('change', this._onMotionChange);
      window.cancelAnimationFrame(this.resizeFrame);
    }

    build() {
      if (this.mutationObserver) this.mutationObserver.disconnect();

      this.track.querySelectorAll('[data-marquee-clone]').forEach((clone) => clone.remove());
      const originals = Array.from(this.track.children);

      if (originals.length && !this.isStatic && !reducedMotion.matches) {
        this.appendSet(originals);

        // Distance between an item and its first copy = one full set,
        // including the gap after the last item.
        const firstClone = this.track.children[originals.length];
        const distance = firstClone.offsetLeft - originals[0].offsetLeft;

        // Keep enough copies that the track never runs out mid-loop.
        let sets = 1;
        while (this.track.scrollWidth < this.clientWidth + distance && sets < 20) {
          this.appendSet(originals);
          sets++;
        }

        this.style.setProperty('--cta-band-marquee-distance', `${distance}px`);
        this.style.setProperty('--cta-band-marquee-duration', `${distance / this.speed}s`);
      }

      this.videoObserver.disconnect();
      this.track.querySelectorAll('video[data-marquee-video]').forEach((video) => {
        this.videoObserver.observe(video);
      });

      if (this.mutationObserver) this.mutationObserver.observe(this.track, { childList: true });
    }

    appendSet(originals) {
      const fragment = document.createDocumentFragment();
      originals.forEach((item) => {
        const clone = item.cloneNode(true);
        clone.setAttribute('data-marquee-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('data-shopify-editor-block');
        fragment.appendChild(clone);
      });
      this.track.appendChild(fragment);
    }
  }

  customElements.define('cta-band-marquee', CtaBandMarquee);
})();
